import { useMemo } from 'react';

import type { ShareChannel as GqlShareChannel } from '@change-corgi/middle-end-gql-schema';

import { useAvailableShareChannels } from '../availableChannels';
import type { ShareChannel, ShareConfig } from '../shared/types';
import { useGetShareShortUrl } from '../urls';

import type { ShareAction } from './actions';
import { useShareActionsCore } from './actions';

// Mirrors the GraphQL `ShareLink` type (apps/middle-end/src/gql/types/ShareLink) —
// reproduced locally because the eslint rule forbids importing non-enum/scalar
// types from `@change-corgi/middle-end-gql-schema`.
export type ShareLink = {
	readonly channel: GqlShareChannel;
	readonly longUrl: string;
	readonly shortUrl: string | null;
	readonly shareId: string | null;
};

export type Options<CH extends ShareChannel = ShareChannel> = {
	config?: ShareConfig;
	disableShortLinks?: boolean;
	trackingEventName: string;
	getUrl: (channel: CH) => string;
	getContent: (channel: CH, type: 'title' | 'body' | 'image', context: { url: string }) => string;
	getTrackingData: (channel: CH) => Readonly<Record<string, boolean | string | number | undefined>>;
	onSuccess?: (channel: CH) => void | Promise<void>;
	onFailure?: (channel: CH) => void | Promise<void>;
};

/**
 * @deprecated Use {@link useShareActions} instead, which consumes server-provided
 * `ShareLinks.links` (with pre-computed shortUrl and shareId) from the middle-end's
 * `Petition.shareLinks` field. Existing call sites can be migrated when their page
 * starts querying `Petition.shareLinks`.
 */
export function useAvailableShareActions<CH extends ShareChannel = ShareChannel>(
	channels: readonly CH[],
	{
		getUrl,
		getTrackingData,
		getContent,
		trackingEventName,
		disableShortLinks,
		onSuccess,
		onFailure,
		config,
	}: Options<CH>,
): ReadonlyArray<ShareAction<CH>> {
	const configWithDefaults = useMemo(() => config || {}, [config]);

	const availableChannels = useAvailableShareChannels(channels, configWithDefaults);

	const getShortUrl = useGetShareShortUrl(availableChannels, { getUrl, disableShortLinks });

	return useShareActionsCore(availableChannels, {
		getUrl,
		getShortUrl,
		getTrackingData,
		getContent,
		trackingEventName,
		onSuccess,
		onFailure,
		config: configWithDefaults,
	});
}

export type UseShareActionsOptions = {
	config?: ShareConfig;
	trackingEventName: string;
	getContent: (channel: ShareChannel, type: 'title' | 'body' | 'image', context: { url: string }) => string;
	getTrackingData: (channel: ShareChannel) => Readonly<Record<string, boolean | string | number | undefined>>;
	onSuccess?: (channel: ShareChannel) => void | Promise<void>;
	onFailure?: (channel: ShareChannel) => void | Promise<void>;
};

/**
 * Builds share actions from server-provided `ShareLinks.links` (typically queried
 * from `Petition.shareLinks` on the middle-end). Long URLs, short URLs, and share
 * IDs all come pre-computed from the server; no client-side URL shortening or
 * shareId extraction happens here.
 *
 * Unlike {@link useAvailableShareActions}, this hook does NOT run
 * `useAvailableShareChannels` — it trusts the server's channel selection.
 * Channels that depend on runtime client capabilities (e.g. `mobileNativeShare`,
 * `sms`, `instagramStories`) will render even when the capability is missing,
 * so callers must ensure the server only returns channels that make sense for
 * the current environment.
 *
 * When a link's `shortUrl` is null (e.g. the server-side shortener failed or didn't run), the
 * action falls back to the `longUrl`.
 */
export function useShareActions(
	links: readonly ShareLink[],
	{ trackingEventName, getContent, getTrackingData, onSuccess, onFailure, config }: UseShareActionsOptions,
): readonly ShareAction[] {
	const configWithDefaults = useMemo(() => config || {}, [config]);

	const byChannel = useMemo(() => new Map(links.map((link) => [link.channel as ShareChannel, link])), [links]);
	const channels = useMemo(() => links.map((link) => link.channel as ShareChannel), [links]);

	const getUrl = useMemo(
		() => (channel: ShareChannel) => {
			const link = byChannel.get(channel);
			if (!link) throw new Error(`useShareActions: no link for channel "${channel}"`);
			return link.shortUrl ?? link.longUrl;
		},
		[byChannel],
	);
	const getShareId = useMemo(
		() => (channel: ShareChannel) => {
			const link = byChannel.get(channel);
			if (!link) throw new Error(`useShareActions: no link for channel "${channel}"`);
			return link.shareId ?? undefined;
		},
		[byChannel],
	);

	return useShareActionsCore(channels, {
		getUrl,
		getShortUrl: getUrl,
		getShareId,
		getTrackingData,
		getContent,
		trackingEventName,
		onSuccess,
		onFailure,
		config: configWithDefaults,
	});
}

export type { ShareAction };
